import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { HlmBadgeComponent, HlmBtnDirective, HlmIconComponent, HlmTooltipDirective } from '../../../ui';
import { CustomerCard, EntitySection } from './unified-search.model';
import { highlightSegments, HighlightSegment, pairMembers } from './search-match';
import { BrandDirectoryService } from './brand-directory.service';
import { BrandLogoComponent } from '../../brands/brand-logo.component';

type StatusVariant = 'success' | 'destructive' | 'secondary' | 'accent' | 'outline';
type GotoTone = 'primary' | 'accent' | 'success' | 'muted';

/** A single deep-link the card can emit — either the customer itself or one of its
 * paired UA/PS members. The parent resolves `commands` via `Router.navigate`. */
export interface CardJump {
  commands: (string | number)[];
  kind: 'customer' | 'useraccount' | 'phoneservice';
  entityId: string;
}

/** Badge tone per status value — mirrors `customerStatusTone()`
 * (shared/utils/customer-status.util.ts), extended with the UA/PS service-status
 * vocabulary (formerService/futureService/neverActiveService) so one map covers both card-level and
 * entity-section statuses (SPARTAN_STYLE_GUIDE §7.2). */
const STATUS_VARIANT: Record<string, StatusVariant> = {
  active: 'success', aktiv: 'success',
  deactivated: 'destructive', deaktiv: 'destructive',
  neverActiveService: 'destructive',
  formerCustomer: 'secondary', formerService: 'secondary',
  futureCustomer: 'accent', futureService: 'accent',
};

/** Exported so the unified-search facet chip bar (EB-3621 Plan 4) can reuse the same status
 * copy for its status quick-filter chips instead of duplicating the map. */
export const STATUS_LABEL: Record<string, string> = {
  active: 'Active', aktiv: 'Active',
  deactivated: 'Deactivated', deaktiv: 'Deactivated',
  neverActiveService: 'Never Active',
  formerCustomer: 'Former', formerService: 'Former',
  futureCustomer: 'Future', futureService: 'Future',
};

const SECTION_ICON_TONE: Record<GotoTone, string> = {
  primary: 'bg-primary/10 text-primary',
  accent: 'bg-accent/15 text-accent',
  success: 'bg-success/10 text-success',
  muted: 'bg-muted text-muted-foreground/70',
};

/**
 * Rich combined result card for the unified search (EB-3621 card redesign). One
 * company/account-centric card: a left brand-logo rail, a header with company-level
 * jump chips (Customer/Contracts/Billing/Business Address/Tariff), and one row per
 * paired UA+PS "member" (a user account together with its linked phone service, if
 * any) each with its own jump-chip group. Clicking anywhere on the card body (not a
 * chip) opens the customer information page; every chip deep-links straight to that
 * entity's own page instead, via the single typed `jump` output.
 */
@Component({
  selector: 'cmt-unified-result-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmIconComponent, HlmBadgeComponent, HlmBtnDirective, HlmTooltipDirective, BrandLogoComponent],
  template: `
    <!-- role="group" (not "button"): the card contains its own real <button> jump chips, and a
         button MUST NOT nest interactive descendants (invalid HTML + contradictory for AT). The
         card body stays mouse-clickable as a convenience; every action it offers is also reachable
         as a labelled chip (Tab) and the whole card is openable from the search list (Arrow+Enter,
         handled by the parent). aria-label gives AT a concise summary before the detail. -->
    <div
      role="group"
      [attr.aria-label]="cardAriaLabel()"
      [class]="cardClass()"
      style="cursor: default"
      (click)="onCardBodyClick()"
    >
      <div class="flex min-w-0 flex-col gap-2">
        <!-- CUSTOMER SECTION: company header + customer jump buttons. Its own hover/focus state,
             so the buttons + blue highlight are independent of the member sections below. -->
        <div
          [class]="customerSectionClass()"
          (mouseenter)="customerHovered.set(true)"
          (mouseleave)="customerHovered.set(false)"
          (focusin)="customerHovered.set(true)"
          (focusout)="onCustomerFocusOut($event)"
        >
          <!-- Header: identity block + contact/address + brand logo (top-right) -->
          <div class="flex items-start justify-between gap-3">
          <div class="flex min-w-0 flex-col gap-0.5">
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span [class]="sectionIconClass('primary')">
              <hlm-icon name="lucideBuilding2" size="0.85rem" />
            </span>
            <span class="truncate font-display text-base font-semibold text-foreground">
              @for (seg of parts(card().name || card().customerId); track $index) {<span [class]="seg.hit ? 'rounded-[3px] bg-primary/15 text-foreground' : ''">{{ seg.text }}</span>}
            </span>
            <span class="font-mono-feat shrink-0 text-sm text-muted-foreground">
              · ID <span class="font-semibold text-foreground">{{ card().customerId }}</span>
            </span>
            <hlm-badge [variant]="statusVariant(card().status)">{{ statusLabel(card().status) }}</hlm-badge>
            @if (isCurrentCustomer()) {
              <!-- The purple variant on purpose: ring-purple is one of the decorative
                   tokens no theme overrides, so the marker keeps its own hue instead of
                   colliding with the green status badges. In the green theme the primary
                   token IS that same green, which is why the marker went unnoticed. -->
              <hlm-badge variant="purple" class="text-sm"
                hlmTooltip="This is the customer you are working on right now">
                <hlm-icon name="lucideMapPin" size="0.75rem" />
                You are here
              </hlm-badge>
            }
            @if (phoneticMatch()) {
              <hlm-badge
                variant="outline"
                class="text-sm text-muted-foreground"
                hlmTooltip="Matched by how the name sounds (Cologne phonetics), not by exact text — e.g. “Maier” also finds “Meyer”."
              >≈ sounds alike</hlm-badge>
            }
          </div>
          @if (card().contactName && card().contactName !== card().name) {
            <span class="flex items-center gap-1.5 truncate text-sm text-muted-foreground">
              <hlm-icon name="lucideUserRound" size="0.85rem" class="shrink-0" />
              <span class="truncate">
                Contact: @for (seg of parts(card().contactName ?? ''); track $index) {<span [class]="seg.hit ? 'rounded-[3px] bg-primary/15 text-foreground' : ''">{{ seg.text }}</span>}
              </span>
            </span>
          }
          @if (card().address) {
            <span class="truncate text-sm text-muted-foreground">
              @for (seg of parts(card().address); track $index) {<span [class]="seg.hit ? 'rounded-[3px] bg-primary/15 text-foreground' : ''">{{ seg.text }}</span>}
            </span>
          }
          </div>
          <div class="flex-none">
            @if (brandDomain()) {
              <cmt-brand-logo [domain]="brandDomain()" heightOverride="2.25rem" />
            } @else {
              <span class="whitespace-nowrap text-sm font-medium text-muted-foreground">{{ card().brand }}</span>
            }
          </div>
          </div>

          <!-- Customer jump chips: a floating panel below the section (no reserved space), shown
               when THIS customer section is hovered/focused. -->
          <div [class]="barClass(customerHovered())">
            <button hlmBtn variant="secondary" size="sm" type="button" class="border border-border" hlmTooltip="Customer information" (click)="onCustomerChip($event, 'information')">Customer</button>
            <button hlmBtn variant="outline" size="sm" type="button" hlmTooltip="Contracts" (click)="onCustomerChip($event, 'contracts')">Contracts</button>
            <button hlmBtn variant="outline" size="sm" type="button" hlmTooltip="Invoice & Billing" (click)="onCustomerChip($event, 'invoice')">Billing</button>
            <button hlmBtn variant="outline" size="sm" type="button" hlmTooltip="Manage Services (SMT)" (click)="onCustomerChip($event, 'smt')">Business Address</button>
            <button hlmBtn variant="outline" size="sm" type="button" hlmTooltip="Tariff Check" (click)="onCustomerChip($event, 'tariffcheck')">Tariff</button>
          </div>
          </div>

          <!-- Members: one merged UA+PS block per member, each its own hover/focus section -->
          @for (member of members(); track $index) {
            <div
              [class]="memberSectionClass($index)"
              (mouseenter)="hoveredMemberIndex.set($index)"
              (mouseleave)="hoveredMemberIndex.set(null)"
              (focusin)="hoveredMemberIndex.set($index)"
              (focusout)="onMemberFocusOut($event, $index)"
            >
              <div class="flex min-w-0 items-start gap-3">
                <div class="flex flex-none items-center gap-1">
                  @if (member.userAccount) {
                    <span [class]="sectionIconClass('accent')">
                      <hlm-icon name="lucideUserRound" size="0.85rem" />
                    </span>
                  }
                  @if (member.phoneService) {
                    <span [class]="sectionIconClass('success')">
                      <hlm-icon name="lucidePhone" size="0.85rem" />
                    </span>
                  } @else if (member.userAccount) {
                    <span [class]="sectionIconClass('muted')">
                      <hlm-icon name="lucidePhoneOff" size="0.85rem" />
                    </span>
                  }
                </div>
                <div class="flex min-w-0 flex-col">
                  <span class="truncate text-base font-medium text-foreground">
                    @if (member.userAccount; as ua) {
                      @for (seg of parts(ua.label); track $index) {<span [class]="seg.hit ? 'rounded-[3px] bg-primary/15 text-foreground' : ''">{{ seg.text }}</span>}
                      @if (member.phoneService; as ps) {
                        <span class="text-muted-foreground"> (</span>@for (seg of parts(ps.label); track $index) {<span [class]="seg.hit ? 'rounded-[3px] bg-primary/15 text-foreground' : ''">{{ seg.text }}</span>}<span class="text-muted-foreground">)</span>
                      }
                    } @else if (member.phoneService; as ps) {
                      @for (seg of parts(ps.label); track $index) {<span [class]="seg.hit ? 'rounded-[3px] bg-primary/15 text-foreground' : ''">{{ seg.text }}</span>}
                    }
                  </span>
                  @if (member.userAccount?.sublabel; as sublabel) {
                    <span class="truncate text-sm text-muted-foreground">
                      @for (seg of parts(sublabel); track $index) {<span [class]="seg.hit ? 'rounded-[3px] bg-primary/15 text-foreground' : ''">{{ seg.text }}</span>}
                    </span>
                  }
                  <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
                    @if (member.userAccount; as ua) {
                      <span class="flex items-center gap-1.5">
                        <span class="text-sm text-muted-foreground">user account</span>
                        <span class="font-mono-feat text-sm text-muted-foreground">ID <span class="font-semibold text-foreground">{{ ua.entityId }}</span></span>
                        <hlm-badge [variant]="statusVariant(ua.status)">{{ statusLabel(ua.status) }}</hlm-badge>
                        @if (isCurrentUserAccount(ua.entityId)) {
                          <hlm-badge variant="purple" class="text-sm"
                            hlmTooltip="This is the user account you are working on right now">
                            <hlm-icon name="lucideMapPin" size="0.75rem" />
                            You are here
                          </hlm-badge>
                        }
                      </span>
                    }
                    @if (member.phoneService; as ps) {
                      <span class="flex items-center gap-1.5">
                        <span class="text-sm text-muted-foreground">phone service</span>
                        <span class="font-mono-feat text-sm text-muted-foreground">ID <span class="font-semibold text-foreground">{{ ps.entityId }}</span></span>
                        <hlm-badge [variant]="statusVariant(ps.status)">{{ statusLabel(ps.status) }}</hlm-badge>
                        @if (isCurrentPhoneService(ps.entityId)) {
                          <hlm-badge variant="purple" class="text-sm"
                            hlmTooltip="This is the phone service you are working on right now">
                            <hlm-icon name="lucideMapPin" size="0.75rem" />
                            You are here
                          </hlm-badge>
                        }
                      </span>
                    }
                  </div>
                </div>
              </div>
              <!-- Member jump chips: a floating panel below the member (no reserved space), shown
                   when THIS member section is hovered/focused. Entity guards keep UA-/PS-only right. -->
              <div [class]="barClass(hoveredMemberIndex() === $index)">
                @if (member.userAccount; as ua) {
                  <button hlmBtn variant="secondary" size="sm" type="button" class="border border-border" hlmTooltip="Account details" (click)="onUaChip($event, ua, 'account-details')">User Account</button>
                }
                @if (member.phoneService; as ps) {
                  <button hlmBtn variant="secondary" size="sm" type="button" class="border border-border" hlmTooltip="Phone service settings" (click)="onPsChip($event, ps, 'settings')">Phone Service</button>
                }
                @if (member.userAccount; as ua) {
                  <button hlmBtn variant="outline" size="sm" type="button" hlmTooltip="Communication Channels" (click)="onUaChip($event, ua, 'communication')">Communication</button>
                }
                @if (member.phoneService; as ps) {
                  <button hlmBtn variant="outline" size="sm" type="button" hlmTooltip="Reachabilities" (click)="onPsChip($event, ps, 'reachabilities')">Reachabilities</button>
                  <button hlmBtn variant="outline" size="sm" type="button" hlmTooltip="CC Numbers" (click)="onPsChip($event, ps, 'numbers')">CC Numbers</button>
                  <button hlmBtn variant="outline" size="sm" type="button" hlmTooltip="Notifications & Forwardings" (click)="onPsChip($event, ps, 'notifications')">Notifications</button>
                  <button hlmBtn variant="outline" size="sm" type="button" hlmTooltip="Contacts" (click)="onPsChip($event, ps, 'contacts')">Contacts</button>
                }
              </div>
            </div>
          }
        </div>
    </div>
  `,
})
export class UnifiedResultCardComponent {
  private readonly brandDirectoryService = inject(BrandDirectoryService);

  /** Per-section hover/focus state: the customer section and each member section reveal their
   * own jump buttons + blue highlight independently (mouseenter/focusin set, mouseleave/focusout
   * clear). Focus reveal keeps buttons keyboard-reachable; touch (no hover/focusin on tap-through)
   * is the remaining gap — accepted for this mouse/keyboard-first internal tool. */
  protected readonly customerHovered = signal(false);
  protected readonly hoveredMemberIndex = signal<number | null>(null);

  readonly card = input.required<CustomerCard>();
  /**
   * Ids of the entities the operator is currently working on. Marking them here
   * stops the "dead chip" reading before it happens: a jump to the page you are
   * already on is a same-URL navigation Angular ignores, so nothing visibly
   * happens (EB-4175 / review M8).
   */
  readonly currentCustomerId = input<string | null>(null);
  readonly currentUserAccountId = input<string | null>(null);
  readonly currentPhoneServiceId = input<string | null>(null);
  readonly selected = input(false);
  /** True when the card's name only matched the query phonetically (not as a substring) — renders the "sounds alike" marker. */
  readonly phoneticMatch = input(false);
  /** The current search query — drives the in-card highlight of the matched substring. */
  readonly query = input('');

  /** Single typed deep-link event — the customer itself, or one of its paired UA/PS members. */
  readonly jump = output<CardJump>();

  /**
   * Resolves the card's brand NAME (all the ES index gives us) to a CDN asset
   * DOMAIN via `BrandDirectoryService`, feeding `cmt-brand-logo`. This makes
   * the `/contract/v2/rest/brands` call genuinely load-bearing for the card —
   * the earlier "the brand API call looks redundant, consider removing it"
   * Boy-Scout note no longer applies now that logos consume it.
   */
  protected readonly brandDomain = computed(() => this.brandDirectoryService.domainForName(this.card().brand));

  /** UA and PS sections paired 1:1 (a UA has 0 or 1 phone service in reality) into member rows. */
  protected readonly members = computed(() => pairMembers(this.card().userAccounts, this.card().phoneServices));

  /** Concise AT summary of the card, announced before its detailed content (company, id, status). */
  protected readonly isCurrentCustomer = computed(() =>
    this.currentCustomerId() !== null && String(this.card().customerId) === this.currentCustomerId(),
  );

  protected isCurrentUserAccount(entityId: string): boolean {
    return this.currentUserAccountId() !== null && String(entityId) === this.currentUserAccountId();
  }

  protected isCurrentPhoneService(entityId: string): boolean {
    return this.currentPhoneServiceId() !== null && String(entityId) === this.currentPhoneServiceId();
  }

  protected readonly cardAriaLabel = computed(() =>
    `${this.card().name || this.card().customerId}, customer ${this.card().customerId}, ${this.statusLabel(this.card().status)}`);

  constructor() {
    this.brandDirectoryService.ensureLoaded();
  }

  protected cardClass(): string {
    // No cursor-pointer: the card body is clickable but the pointer cursor is reserved for the
    // jump buttons. The whole-card ring is only the keyboard-selected state; hover highlights are
    // per-section (customerSectionClass / memberSectionClass) so each area lights up on its own.
    // `relative` + a raised z-index while a section is hovered so the floating button panel
    // overlays the sibling cards below instead of being painted under them.
    const anyHovered = this.customerHovered() || this.hoveredMemberIndex() !== null;
    const base = `group relative flex flex-col gap-2 rounded-xl border border-border bg-card p-3 shadow-sm transition-colors${anyHovered ? ' z-20' : ''}`;
    return this.selected()
      ? `${base} bg-primary/10 ring-2 ring-primary/25`
      : base;
  }

  /** Customer section chrome — `relative` anchors its floating button panel; blue tint on hover. */
  protected customerSectionClass(): string {
    const base = 'relative flex flex-col gap-2 rounded-lg px-2 py-1.5 transition-colors';
    return this.customerHovered() ? `${base} bg-primary/10` : base;
  }

  /** Member section chrome — `relative` anchors its floating button panel; blue tint on hover. */
  protected memberSectionClass(index: number): string {
    const base = 'relative flex flex-col gap-1.5 rounded-lg border-t border-dashed border-border px-2 pb-1.5 pt-2 transition-colors';
    return this.hoveredMemberIndex() === index ? `${base} bg-primary/10` : base;
  }

  /** Floating jump-button panel: absolutely positioned just below its section so it costs no
   * layout space (card stays compact, no shift), overlaying the content below on hover. */
  protected barClass(visible: boolean): string {
    // `-mt-1` overlaps the section's bottom padding so there's NO gap between the section and its
    // panel — the panel is a DOM descendant, so with no non-descendant strip in between, moving
    // the pointer onto it doesn't fire the section's mouseleave (the panel stays reachable).
    const base = 'absolute left-2 top-full z-30 -mt-1 flex w-max max-w-[calc(100%-1rem)] flex-wrap items-center gap-1.5 rounded-lg border border-border bg-popover p-1.5 shadow-lg transition-opacity duration-150';
    return visible ? base : `${base} pointer-events-none opacity-0`;
  }

  /** Splits `text` into plain/highlighted segments against the current query — feeds the `@for` highlight blocks. */
  protected parts(text: string): HighlightSegment[] {
    return highlightSegments(text, this.query());
  }

  protected statusVariant(status: string): StatusVariant {
    return STATUS_VARIANT[status] ?? 'outline';
  }

  protected statusLabel(status: string): string {
    return STATUS_LABEL[status] ?? status;
  }

  protected sectionIconClass(tone: GotoTone): string {
    return `grid h-8 w-8 flex-none place-items-center rounded-lg ${SECTION_ICON_TONE[tone]}`;
  }

  /** Card body click (anywhere but a chip) — jumps to the customer's information page. */
  protected onCardBodyClick(): void {
    this.emitJump('customer', this.card().customerId, ['/', this.card().customerId, 'information']);
  }

  /** Collapse the customer section's chips only when focus leaves that section entirely. */
  protected onCustomerFocusOut(event: FocusEvent): void {
    if (this.focusLeftSection(event)) this.customerHovered.set(false);
  }

  /** Collapse a member section's chips only when focus leaves that member section entirely. */
  protected onMemberFocusOut(event: FocusEvent, index: number): void {
    if (this.focusLeftSection(event) && this.hoveredMemberIndex() === index) {
      this.hoveredMemberIndex.set(null);
    }
  }

  /** True when focus moved outside the section element that emitted the focusout. */
  private focusLeftSection(event: FocusEvent): boolean {
    const section = event.currentTarget as HTMLElement;
    const next = event.relatedTarget as Node | null;
    return !next || !section.contains(next);
  }

  protected onCustomerChip(event: Event, routeSegment: string): void {
    event.stopPropagation();
    this.emitJump('customer', this.card().customerId, ['/', this.card().customerId, routeSegment]);
  }

  protected onUaChip(event: Event, ua: EntitySection, routeSegment: string): void {
    event.stopPropagation();
    this.emitJump('useraccount', ua.entityId, ['/', this.card().customerId, 'ua', ua.entityId, routeSegment]);
  }

  protected onPsChip(event: Event, ps: EntitySection, routeSegment: string): void {
    event.stopPropagation();
    this.emitJump('phoneservice', ps.entityId, ['/', this.card().customerId, 'ps', ps.entityId, routeSegment]);
  }

  private emitJump(kind: CardJump['kind'], entityId: string, commands: (string | number)[]): void {
    this.jump.emit({ kind, entityId, commands });
  }
}
